import posthog, { PostHog } from 'posthog-js'
import { service } from '@ember/service'
import config from 'customer-flows/config/environment'
import Logger from 'customer-flows/utils/logger'
import { Tracker } from 'customer-flows/services/trackers/tracker'
import { getDeviceType, isMobileDevice } from 'customer-flows/utils/is-mobile-device'
import { context } from 'customer-flows/utils/core'
import { tracked } from '@glimmer/tracking'
import { assert } from '@ember/debug'

export const FF_REQUEST_TIMEOUT = 1000
export const FF_REQUEST_TIMEOUT_MOBILE = 3000

type ExperimentFlag = 'nextgen-conversion' | 'reviews' | 'close-confirmation-popup'

export const KNOWN_EXPERIMENT_FLAGS: ExperimentFlag[] = [
  'nextgen-conversion',
  'reviews',
  'close-confirmation-popup'
]

const LOCAL_DEV_STORAGE_KEY = 'debugToolbarPosthogLocalDev'

type LocalDevState = {
  enabled: boolean
  overrides: Partial<Record<ExperimentFlag, string>>
}

const DEFAULT_LOCAL_DEV_STATE: LocalDevState = { enabled: false, overrides: {} }

export default class PosthogService extends Tracker {
  @service session: Session
  @context('account') isFeatureEnabled: AccountStore['isFeatureEnabled']
  @service declare application: Application

  @tracked private posthogInstance?: PostHog

  @tracked private localDevState: LocalDevState = this._readLocalDevState()

  /**
   * Booking funnel events sent to PostHog from the NextGen flow.
   *
   * The legacy flow keeps its own copy of this list in
   * `main/app/services/posthog.js` (`POSTHOG_EVENT_WHITELIST`).
   * Both lists must be kept in sync for the two flows to be comparable.
   */
  supportedEvents: TrackingEventName[] = [
    'Start',
    'Complete',

    // Milestones
    'Selected Calendar Date',
    'Selected Duration',
    'time_selected',
    'Component Seen: Input Info',
    'questions_shown',
    'Component Seen: Input Billing',

    // Availability
    'availability_available',
    'availability_empty',
    'availability_error',

    // Payment
    'Start Submission'
  ]

  get isEnabled(): boolean {
    return !!this.posthogInstance || this.localDevState.enabled
  }

  override async setup(): Promise<void> {
    if (this.application.isDemoMode) return

    const posthogKey = config.posthog?.key
    const canSetup = this.isFeatureEnabled('posthog') && posthogKey

    if (!canSetup || this.isEnabled) return

    try {
      this.posthogInstance = posthog.init(posthogKey, { api_host: 'https://app.posthog.com' })
      this.posthogInstance.setPersonPropertiesForFlags({ $device_type: getDeviceType() }, false)
      await this._featureFlagsLoaded()
    } catch (error) {
      Logger.error('Failed to initialize Posthog or load feature flags', { error })
      this.posthogInstance = undefined
    }
  }

  enableForLocalDev(): void {
    assert('enableForLocalDev is not available outside local development', !!config.devtools?.enabled)
    if (!config.devtools?.enabled) return

    this._setLocalDevState({ ...this.localDevState, enabled: true })
  }

  private _readLocalDevState(): LocalDevState {
    if (!config.devtools?.enabled) return DEFAULT_LOCAL_DEV_STATE

    const raw = this.session.restoreShared({ useLocalStorage: true })[LOCAL_DEV_STORAGE_KEY]

    try {
      return raw ? JSON.parse(raw) : DEFAULT_LOCAL_DEV_STATE
    } catch {
      return DEFAULT_LOCAL_DEV_STATE
    }
  }

  private _setLocalDevState(state: LocalDevState): void {
    this.localDevState = state
    this.session.persistShared({ [LOCAL_DEV_STORAGE_KEY]: JSON.stringify(state) }, { useLocalStorage: true })
  }

  override track<E extends TrackingEventName>(event: E, props?: EventProps<E>): void {
    this.posthogInstance?.capture(event, { ...props, flow: 'nextgen' })
  }

  /**
   * Checks if the current user has been assigned a specific variant of an experiment flag.
   *
   * @param {ExperimentFlag} experimentFlag - The feature flag to check.
   * @param {'test' | 'control'} variant - The variant name to check against the user's assigned variant.
   * @param {boolean} [sendEvent=true] - Reports the user's enrollment in the experiment to PostHog.
   * @returns {boolean | null}
   *  - `true` if the user's assigned variant matches the specified variant,
   *  - `false` if it does not match
   *  - `null` if Posthog is not initialized or the feature flag is not found.
   */
  isVariantFor(
    experimentFlag: ExperimentFlag,
    variant: 'test' | 'control',
    { sendEvent = true }: { sendEvent?: boolean } = {}
  ): boolean | null {
    if (this.application.isDemoMode) return null

    const assignedVariant = this.localDevState.enabled
      ? this.localDevState.overrides[experimentFlag] ?? null
      : this.posthogInstance?.getFeatureFlag(experimentFlag, { send_event: sendEvent })

    if (assignedVariant === undefined || assignedVariant === null) return null

    return assignedVariant === variant
  }

  // useful for testing that we're using correct timeout depending on device type from our tests
  isMobile(): boolean {
    return isMobileDevice()
  }

  getAssignedVariant(experimentFlag: ExperimentFlag): string | boolean | null {
    if (this.localDevState.enabled) {
      return this.localDevState.overrides[experimentFlag] ?? null
    }

    return this.posthogInstance?.getFeatureFlag(experimentFlag, { send_event: false }) ?? null
  }

  overrideVariant(experimentFlag: ExperimentFlag, variant: string): void {
    if (this.localDevState.enabled) {
      this._setLocalDevState({
        ...this.localDevState,
        overrides: { ...this.localDevState.overrides, [experimentFlag]: variant }
      })
      return
    }

    this.posthogInstance?.featureFlags.overrideFeatureFlags({ flags: { [experimentFlag]: variant } })
  }

  clearVariantOverrides(): void {
    if (this.localDevState.enabled) {
      this._setLocalDevState({ ...this.localDevState, overrides: {} })
      return
    }

    this.posthogInstance?.featureFlags.overrideFeatureFlags(false)
  }

  /**
   * Waits for PostHog feature flags to be loaded. Because PostHog does not expose
   * hooks for failed API requests, this function implements a manual timeout.
   * The promise will reject if the timeout expires before the `onFeatureFlags` callback
   * is invoked.
   *
   * @returns {Promise} A promise that resolves when feature flags are loaded or rejects with an error if the initialization times out.
   */
  private async _featureFlagsLoaded(): Promise<void> {
    const featureFlagsTimeout = this.isMobile() ? FF_REQUEST_TIMEOUT_MOBILE : FF_REQUEST_TIMEOUT

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Posthog initialization timeout'))
      }, featureFlagsTimeout)

      this.posthogInstance?.onFeatureFlags(() => {
        clearTimeout(timeout)
        resolve()
      })
    })
  }
}

declare module '@ember/service' {
  interface Registry {
    posthog: PosthogService
  }
}

declare global {
  export interface PosthogTracker extends PosthogService {}
}

